import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Receipt,
  Users,
  Link2,
  Calculator,
  Settings2,
  LogOut,
  FileSpreadsheet,
  Menu,
} from "lucide-react";
import { Toaster } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export const LOGO_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_otaku-checkout/artifacts/lnjvv7pq_Yellow%20Black%20Brush%20Streetwear%20Brand%20Logo%282%29.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/pos", label: "POS", icon: ShoppingCart, testid: "nav-pos" },
  { to: "/orders", label: "Orders", icon: Receipt, testid: "nav-orders" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/catalogs", label: "Catalogs", icon: Link2, testid: "nav-catalogs" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/delivery-estimate", label: "Delivery Est.", icon: Calculator, testid: "nav-delivery-estimate" },
  { to: "/reports", label: "Reports", icon: FileSpreadsheet, testid: "nav-reports" },
  { to: "/settings", label: "Settings", icon: Settings2, testid: "nav-settings" },
];

function NavItems({ onNavigate, testidPrefix = "" }) {
  return (
    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          data-testid={`${testidPrefix}${item.testid}`}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 font-medium text-sm transition-colors ${
              isActive
                ? "bg-[color:var(--mecha-red)] text-white"
                : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
            }`
          }
        >
          <item.icon className="h-4 w-4 shrink-0" strokeWidth={2.5} />
          <span className="uppercase tracking-wider text-xs">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarFooter({ user, onLogout }) {
  return (
    <div className="border-t border-zinc-800 p-4 shrink-0">
      <div className="text-xs text-zinc-500 font-mono mb-2">SIGNED IN AS</div>
      <div className="text-sm font-medium truncate" data-testid="current-user-email">
        {user?.email}
      </div>
      <button
        data-testid="logout-btn"
        onClick={onLogout}
        className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 border border-zinc-800 hover:bg-white hover:text-black text-xs font-mono uppercase tracking-widest transition-colors"
      >
        <LogOut className="h-3.5 w-3.5" />
        Log Out
      </button>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const currentLabel =
    NAV.find((n) =>
      n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to)
    )?.label || "Menu";

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0f0f10",
            border: "1px solid #27272a",
            borderRadius: 0,
            color: "white",
            fontFamily: "'Space Grotesk', sans-serif",
          },
        }}
      />

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 border-b border-zinc-800 bg-black/95 backdrop-blur flex items-center gap-3 px-3 py-3">
        <button
          type="button"
          data-testid="mobile-menu-btn"
          onClick={() => setMenuOpen(true)}
          className="h-10 w-10 border border-zinc-800 flex items-center justify-center hover:border-white shrink-0"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="w-9 h-9 bg-white p-0.5 shrink-0">
          <img src={LOGO_URL} alt="Swagg Squad" className="w-full h-full object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm leading-none truncate">
            SWAGG<span className="text-[color:var(--mecha-red)]">.</span>SQUAD
          </div>
          <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 mt-1 truncate">
            {currentLabel.toUpperCase()}
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="left"
          className="w-[min(100%,20rem)] p-0 bg-black border-zinc-800 flex flex-col gap-0"
        >
          <SheetHeader className="px-5 py-5 border-b border-zinc-800 text-left space-y-0">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-white p-1 shrink-0">
                <img src={LOGO_URL} alt="" className="w-full h-full object-contain" />
              </div>
              <SheetTitle className="font-display text-lg text-white leading-none">
                SWAGG<span className="text-[color:var(--mecha-red)]">.</span>SQUAD
              </SheetTitle>
            </div>
          </SheetHeader>
          <NavItems onNavigate={() => setMenuOpen(false)} testidPrefix="mobile-" />
          <SidebarFooter user={user} onLogout={handleLogout} />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-zinc-800 bg-black flex-col min-h-screen sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-zinc-800 flex items-center gap-3">
          <div className="w-12 h-12 bg-white p-1 shrink-0">
            <img src={LOGO_URL} alt="Swagg Squad" className="w-full h-full object-contain" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg leading-none truncate">
              SWAGG<span className="text-[color:var(--mecha-red)]">.</span>SQUAD
            </div>
            <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 mt-1.5">
              POS TERMINAL v1.1
            </div>
          </div>
        </div>
        <NavItems />
        <SidebarFooter user={user} onLogout={handleLogout} />
      </aside>

      <main className="flex-1 min-w-0 grid-lines overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
